import React, { useEffect, useState } from "react";
import { FamilyTree } from "./FamilyTree";
import { TreeErrorBoundary } from "./TreeErrorBoundary";
import { MemberSheet } from "./MemberSheet";
import { ContributeDialog } from "./ContributeDialog";
import { Button } from "./ui/button";
import { api, photoUrl } from "../lib/api";
import { copyText } from "../lib/clipboard";
import { toast } from "sonner";
import { LogIn, HeartHandshake, Share2, Lock } from "lucide-react";

const noop = () => {};

export function PublicTreeView({ slug }) {
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [selectedId, setSelectedId] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [contributeOpen, setContributeOpen] = useState(false);

  useEffect(() => {
    api.publicTree(slug)
      .then((data) => setState({ loading: false, error: null, data }))
      .catch(() => setState({ loading: false, error: "This family tree is private or does not exist.", data: null }));
  }, [slug]);

  const goSignIn = () => { window.location.href = "/"; };
  const shareLink = async () => {
    const ok = await copyText(window.location.href);
    ok ? toast.success("Link copied") : toast.error("Could not copy the link");
  };

  if (state.loading) {
    return <div className="flex h-screen items-center justify-center bg-[#F4F6F8] text-[#64748B]">Loading family tree…</div>;
  }
  if (state.error) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4 bg-[#F4F6F8] px-6 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-[#E8EEF3] text-[#082D4A]"><Lock size={28} /></div>
        <h1 className="font-serif text-2xl font-bold text-[#082D4A]">This tree is private</h1>
        <p className="max-w-md text-sm text-[#64748B]">{state.error} Ask the family owner for an invite, or create your own family tree.</p>
        <Button onClick={goSignIn} data-testid="public-go-home" className="rounded-full bg-[#D4A017] text-white hover:bg-[#B0850F]">Go to Where Am I From</Button>
      </div>
    );
  }

  const { family, members, groups, frames, settings } = state.data;
  const bgUrl = settings?.tree_bg_path ? photoUrl(settings.tree_bg_path) : null;
  const selected = members.find((m) => m.id === selectedId) || null;

  return (
    <div className="flex h-screen flex-col bg-[#F4F6F8]">
      <header className="z-30 flex flex-wrap items-center justify-between gap-3 border-b border-[#D8E0E8] bg-white/80 px-5 py-3 backdrop-blur">
        <div className="flex items-center gap-2.5">
          <img src="/logo-mark.png" alt="" className="h-10 w-10 object-contain" />
          <div className="leading-none">
            <div className="font-brand text-lg leading-none text-[#082D4A]">{family?.name || "Family tree"}</div>
            <div className="mt-1 flex items-center gap-1 text-[10px] font-medium uppercase tracking-[0.15em] text-[#64748B]">
              <Share2 size={10} /> Read-only shared tree
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={shareLink} data-testid="public-share-button" variant="outline" className="rounded-full border-[#D8E0E8] bg-white text-[#082D4A] hover:bg-[#E8EEF3]">
            <Share2 size={16} className="sm:mr-1.5" /><span className="hidden sm:inline">Share</span>
          </Button>
          <Button onClick={() => setContributeOpen(true)} data-testid="public-contribute-button" variant="outline" className="rounded-full border-[#D4A017]/40 bg-white text-[#D4A017] hover:bg-[#D4A017]/5">
            <HeartHandshake size={16} className="sm:mr-1.5" /><span className="hidden sm:inline">Ask to contribute</span>
          </Button>
          <Button onClick={goSignIn} data-testid="public-signin-button" variant="ghost" className="rounded-full text-[#082D4A]">
            <LogIn size={16} className="sm:mr-1.5" /><span className="hidden sm:inline">Sign in</span>
          </Button>
        </div>
      </header>

      <main className="relative flex-1 overflow-hidden">
        {members.length === 0 ? (
          <div className="flex h-full items-center justify-center text-[#64748B]">This family hasn’t added anyone yet.</div>
        ) : (
          <TreeErrorBoundary>
          <FamilyTree
            members={members}
            onSelect={(id) => { setSelectedId(id); setSheetOpen(true); }}
            selectedId={selectedId}
            onChanged={noop}
            canEdit={false}
            isAdmin={false}
            bgUrl={bgUrl}
            onBgChanged={noop}
            onAddRelative={noop}
            lineageColors={settings?.lineage_colors || {}}
            onSettingsChanged={noop}
            groups={groups}
            onGroupsChanged={noop}
            frames={frames}
            onFramesChanged={noop}
          />
          </TreeErrorBoundary>
        )}
      </main>

      <MemberSheet
        member={selected}
        members={members}
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        onAddRelative={noop}
        onChanged={noop}
        onSelect={(id) => { setSelectedId(id); setSheetOpen(true); }}
        onPlayStory={noop}
        canEdit={false}
        onRequireLogin={goSignIn}
        onRequestContribute={() => setContributeOpen(true)}
      />
      <ContributeDialog open={contributeOpen} onOpenChange={setContributeOpen} family={slug} />
    </div>
  );
}
